/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import PDFDocument from "@react-pdf/pdfkit";
import { convertImageUrlToBase64 } from "@src/appV2/Agents/components/ContractorBadge/utils/convertImageUrlToBase64";
import { isEmpty } from "lodash";
import { v4 as uuid } from "uuid";

import { WORK_BADGE_PDF_DEFAULT_SETTINGS } from "../constants/badgePdf";
import { getBadgeDisplayName, getOptimalFontSettings, mmToPoints } from "./badgePdf";

export interface CreateWorkBadgePdfParams {
  workerDetails: {
    firstName: string;
    lastName: string;
    qualificationName: string;
    profilePictureBase64?: string;
  };
  /**
   * Page size for the PDF output - "A4" or [width, height]
   */
  pageSize?: "A4" | [number, number];
  /**
   * Margin around the badge in millimeters. Useful when outputting to standard page sizes
   * (like A4) to provide spacing since the badge will be positioned at the top-left.
   * Allows room for cutting the badge out of paper without damaging borders.
   * @default WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.BADGE_MARGIN_IN_MM
   */
  badgeMarginInMm?: number;
}

export async function createWorkBadgePdf(params: CreateWorkBadgePdfParams): Promise<{
  blob: Blob;
  pdfKey: string;
}> {
  const { workerDetails, badgeMarginInMm, pageSize } = params;

  const badgeMargin = isDefined(badgeMarginInMm)
    ? mmToPoints(badgeMarginInMm)
    : mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.BADGE_MARGIN_IN_MM);
  const badgeBorderWidth = mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.BORDER_WIDTH_IN_MM);

  const badge = {
    // PDFKit renders borders centered on rectangle edges (half inside, half outside)
    // Subtract border width so total size including border equals credit card dimensions
    width: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.WIDTH_IN_MM) - badgeBorderWidth, // Credit card size minus border (border is centered)
    height: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.HEIGHT_IN_MM) - badgeBorderWidth,
    padding: {
      top: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.PADDING_IN_MM),
      right: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.PADDING_IN_MM),
      bottom: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.PADDING_IN_MM),
      left: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.PADDING_IN_MM),
    },
    border: {
      width: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.BORDER_WIDTH_IN_MM),
      radius: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.BORDER_RADIUS_IN_MM),
    },
    margin: {
      top: badgeMargin,
      right: badgeMargin,
      bottom: badgeMargin,
      left: badgeMargin,
    },
    // Will be calculated
    x: 0,
    y: 0,
  };

  // Badge position and size within the document
  badge.x = badge.margin.left + badge.border.width / 2;
  badge.y = badge.margin.top + badge.border.width / 2;

  // Use same document size as badge size if not specified
  // Total size = credit card size + margins (border already accounted for in rectangle sizing)
  const defaultSize = [
    mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.WIDTH_IN_MM) +
      badge.margin.left +
      badge.margin.right,
    mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.BADGE.HEIGHT_IN_MM) +
      badge.margin.top +
      badge.margin.bottom,
  ]; // [width, height]

  const pdf = new PDFDocument({
    size: pageSize ?? defaultSize,
    margins: { top: 0, right: 0, bottom: 0, left: 0 }, // Custom margin control
  });

  // --- Badge background with rounded corners
  pdf
    .lineWidth(badge.border.width)
    .roundedRect(badge.x, badge.y, badge.width, badge.height, badge.border.radius)
    .fillAndStroke(
      WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.BACKGROUND,
      WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.FOREGROUND
    );

  const defaultProfilePictureBase64 = await convertImageUrlToBase64(
    WORK_BADGE_PDF_DEFAULT_SETTINGS.WORKER_PICTURE.DEFAULT_URL
  );

  // --- Profile picture
  const profileImage = {
    x: badge.x + badge.padding.left,
    y: badge.y + badge.padding.top,
    width: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.WORKER_PICTURE.WIDTH_IN_MM),
    height: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.WORKER_PICTURE.HEIGHT_IN_MM),
    radius: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.WORKER_PICTURE.BORDER_RADIUS_IN_MM),
    margin: {
      right: mmToPoints(WORK_BADGE_PDF_DEFAULT_SETTINGS.WORKER_PICTURE.MARGIN_RIGHT_IN_MM),
    },
  };

  // Save graphics state and create clipping mask for rounded image
  pdf
    .save()
    .lineWidth(0)
    .roundedRect(
      profileImage.x,
      profileImage.y,
      profileImage.width,
      profileImage.height,
      profileImage.radius
    )
    .clip()
    .image(
      !isDefined(workerDetails.profilePictureBase64) || isEmpty(workerDetails.profilePictureBase64)
        ? defaultProfilePictureBase64
        : workerDetails.profilePictureBase64,
      profileImage.x,
      profileImage.y,
      {
        width: profileImage.width,
        height: profileImage.height,
        cover: [profileImage.width, profileImage.height],
        align: "center",
        valign: "center",
      }
    )
    .restore();

  // --- Name
  const displayName = getBadgeDisplayName({
    firstName: workerDetails.firstName,
    lastName: workerDetails.lastName,
  });

  const nameText = {
    x: profileImage.x + profileImage.width + profileImage.margin.right,
    y: profileImage.y + mmToPoints(1),
    size: {
      min: mmToPoints(4.9),
      max: mmToPoints(7.1),
    },
    lineGap: mmToPoints(-0.7),
    font: WORK_BADGE_PDF_DEFAULT_SETTINGS.TEXT.FONTS.BOLD,
  };

  // Calculate available width and height for name text
  const nameAvailableWidth = badge.x + badge.width - nameText.x - badge.padding.right;
  const nameAvailableHeight = mmToPoints(15);

  const nameOptimal = getOptimalFontSettings({
    pdf,
    text: displayName,
    maxWidth: nameAvailableWidth,
    maxHeight: nameAvailableHeight,
    font: nameText.font,
    minFontSize: nameText.size.min,
    maxFontSize: nameText.size.max,
    lineGap: nameText.lineGap,
  });

  // Add name text to the right of the image
  pdf
    .font(nameText.font)
    .fontSize(nameOptimal.fontSize)
    .fillColor(WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.FOREGROUND)
    .text(displayName, nameText.x, nameText.y, {
      align: "left",
      width: nameAvailableWidth, // Force text wrapping within available width
      lineGap: nameText.lineGap,
    });

  // --- Qualification
  const qualificationText = {
    background: {
      x: nameText.x,
      y: nameText.y + nameOptimal.actualHeight + mmToPoints(1),
      radius: mmToPoints(8),
      padding: {
        horizontal: mmToPoints(3),
        vertical: mmToPoints(1.5),
      },
      // Will be calculated
      width: 0,
      height: 0,
    },
    size: {
      min: mmToPoints(2.9),
      max: mmToPoints(3.9),
    },
    lineGap: mmToPoints(-0.3),
    font: WORK_BADGE_PDF_DEFAULT_SETTINGS.TEXT.FONTS.REGULAR,
  };

  // Calculate available width and height for qualification text
  const qualificationAvailableWidth = badge.x + badge.width - nameText.x - badge.padding.right;
  const qualificationAvailableHeight = mmToPoints(10);
  const qualificationMaxTextWidth =
    qualificationAvailableWidth - qualificationText.background.padding.horizontal * 2;

  const qualificationOptimal = getOptimalFontSettings({
    pdf,
    text: workerDetails.qualificationName,
    maxWidth: qualificationMaxTextWidth,
    maxHeight: qualificationAvailableHeight,
    font: qualificationText.font,
    minFontSize: qualificationText.size.min,
    maxFontSize: qualificationText.size.max,
    lineGap: qualificationText.lineGap,
  });

  // Calculate background dimensions with optimal font size
  pdf.font(qualificationText.font).fontSize(qualificationOptimal.fontSize);
  const qualificationWidth = pdf.widthOfString(workerDetails.qualificationName);

  qualificationText.background.width = Math.min(
    qualificationWidth + qualificationText.background.padding.horizontal * 2,
    qualificationAvailableWidth
  );
  qualificationText.background.height =
    qualificationOptimal.actualHeight + qualificationText.background.padding.vertical * 2;

  // Add qualification background
  pdf
    .lineWidth(0)
    .roundedRect(
      qualificationText.background.x,
      qualificationText.background.y,
      qualificationText.background.width,
      qualificationText.background.height,
      qualificationText.background.radius
    )
    .fillAndStroke(
      WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.FOREGROUND,
      WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.FOREGROUND
    );

  // Add qualification text
  const qualificationTextX =
    qualificationText.background.x + qualificationText.background.padding.horizontal;

  const qualificationTextY =
    qualificationText.background.y +
    (qualificationText.background.height - qualificationOptimal.actualHeight) / 2 +
    qualificationOptimal.fontSize * 0.16; // Correction for baseline alignment

  pdf
    .font(qualificationText.font)
    .fontSize(qualificationOptimal.fontSize)
    .fillColor(WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.BACKGROUND)
    .text(workerDetails.qualificationName, qualificationTextX, qualificationTextY, {
      align: "left",
      width: qualificationMaxTextWidth,
      lineGap: qualificationText.lineGap,
    });

  // --- Bottom Bar (full-width dark bar with rounded bottom corners)
  const bottomBar = {
    height: mmToPoints(11),
    padding: {
      horizontal: mmToPoints(4),
      vertical: mmToPoints(2),
    },
    // Will be calculated
    x: 0,
    y: 0,
    width: 0,
  };

  // Calculate bottom bar dimensions - spans full width inside the badge border
  bottomBar.x = badge.x;
  bottomBar.width = badge.width;
  bottomBar.y = badge.y + badge.height - bottomBar.height;

  // Draw bottom bar with rounded corners only on bottom-left and bottom-right
  // Use clipping to achieve rounded bottom corners matching the badge
  const barRadius = badge.border.radius;
  pdf
    .save()
    // Create a clipping path using the badge's rounded rectangle shape
    .roundedRect(badge.x, badge.y, badge.width, badge.height, barRadius)
    .clip()
    // Draw the bottom bar rectangle (will be clipped to rounded corners at bottom)
    .rect(bottomBar.x, bottomBar.y, bottomBar.width, bottomBar.height)
    .fill(WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.FOREGROUND)
    .restore();

  // --- Independent Contractor text (left side of bottom bar)
  const independentContractorText = {
    content: "Independent Contractor",
    fontSize: mmToPoints(3.5),
    font: WORK_BADGE_PDF_DEFAULT_SETTINGS.TEXT.FONTS.REGULAR,
    x: bottomBar.x + bottomBar.padding.horizontal,
    y: bottomBar.y + bottomBar.height / 2,
  };

  pdf
    .font(independentContractorText.font)
    .fontSize(independentContractorText.fontSize)
    .fillColor(WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.BACKGROUND)
    .text(
      independentContractorText.content,
      independentContractorText.x,
      independentContractorText.y,
      {
        align: "left",
        baseline: "middle",
      }
    );

  // --- CLIPBOARD text and logo (right side of bottom bar)
  const logoBase64 = await convertImageUrlToBase64(
    WORK_BADGE_PDF_DEFAULT_SETTINGS.CLIPBOARD_LOGO.DEFAULT_URL
  );

  // Logo dimensions
  const logoImageHeight = mmToPoints(8);
  const logoImageWidth = logoImageHeight * (14 / 15); // Maintain aspect ratio

  const logoImage = {
    x: bottomBar.x + bottomBar.width - bottomBar.padding.horizontal - logoImageWidth,
    y: bottomBar.y + (bottomBar.height - logoImageHeight) / 2,
    width: logoImageWidth,
    height: logoImageHeight,
  };

  pdf.image(logoBase64, logoImage.x, logoImage.y, {
    width: logoImage.width,
    height: logoImage.height,
    fit: [logoImage.width, logoImage.height],
  });

  // CLIPBOARD text (to the left of the logo)
  const clipboardText = {
    content: "CLIPBOARD",
    fontSize: mmToPoints(3.5),
    font: WORK_BADGE_PDF_DEFAULT_SETTINGS.TEXT.FONTS.BOLD,
    marginRight: mmToPoints(1.5),
  };

  pdf.font(clipboardText.font).fontSize(clipboardText.fontSize);
  const clipboardTextWidth = pdf.widthOfString(clipboardText.content);

  const clipboardTextX = logoImage.x - clipboardTextWidth - clipboardText.marginRight;
  const clipboardTextY = bottomBar.y + bottomBar.height / 2;

  pdf
    .font(clipboardText.font)
    .fontSize(clipboardText.fontSize)
    .fillColor(WORK_BADGE_PDF_DEFAULT_SETTINGS.PALETTE.PRIMARY.BACKGROUND)
    .text(clipboardText.content, clipboardTextX, clipboardTextY, {
      align: "left",
      baseline: "middle",
    });

  // Collect the chunks of the PDF and return a blob
  const chunks: Uint8Array[] = [];

  pdf.on("data", (chunk: Uint8Array) => {
    chunks.push(chunk);
  });

  const promise = new Promise<{ blob: Blob; pdfKey: string }>((resolve, reject) => {
    pdf.on("end", () => {
      // Convert Uint8Array chunks to proper ArrayBuffer type for TypeScript
      // Create new Uint8Array instances to ensure proper ArrayBuffer type
      const blobParts = chunks.map((chunk) => new Uint8Array(chunk).buffer);
      const blob = new Blob(blobParts, { type: "application/pdf" });
      resolve({ blob, pdfKey: uuid() });
    });

    pdf.on("error", (error) => {
      reject(error);
    });
  });

  pdf.end();

  return await promise;
}
/* eslint-enable max-lines */
